import {ItemListViewProps} from "../../types/ItemListViewProps.ts";
import VCarouselHolder from "./VCarouselStyles.tsx";
import {useEffect, useState} from "react";
import {truncateWords} from "../../utils/search-utils.ts";
import {loadAppropriateImages} from "../../utils/image-utils.ts";
import GetLabel from "../../language/GetLabel.tsx";
import {getLabel} from "../../language/lang-utils.ts";
import { getLanguage } from "../../language/lang-utils.ts";
import { DateTime } from "luxon";

const VCarousel = (props: ItemListViewProps) => {
    const {itemList} = props
    const [page, setPage] = useState(0)

    // Hooks must run unconditionally — this was below the early return (a rules-of-hooks
    // violation). Hydrates the lazy data-src thumbnails after each page render.
    useEffect(() => {
        loadAppropriateImages()
    });

    if (itemList === undefined) {
        console.warn("No item list to render in VCarousel")
        return
    }
    const pageSize = itemList.pageSize == 0 ? 10 : itemList.pageSize
    // This could be parameterized...
    const maxPages = 5;
    const lang = getLanguage();

    const isLastPage = () => {
        const pgs = Math.ceil(itemList.items.length / pageSize)
        const max = Math.min(pgs, maxPages)
        return page == (max - 1) // page is a 0-based array index
    }

    const showPrev = () => {
        setPage(page => {
            if (page == 0) return 0;
            return page - 1
        })
    }

    const showNext = () => {
        setPage(page => {
            if (!isLastPage())
                return page + 1
            else return 0
        })
    }

    const renderItems = () => {
        const selected = itemList.items.slice(page * pageSize, (page + 1) * pageSize);
        return selected.map((item, i) => {
            if (item.image === undefined || item.title === undefined) return;

            const itemLiveTimeEN = item.liveDateTime && item.type == 'video' ? item.liveDateTime.toUTC().setLocale('en') : item.liveDateTime && (item.type == 'live' || item.type == 'prelive') ? item.liveDateTime.setLocale('en') : "";
            const itemLiveTimeFR = item.liveDateTime && item.type == 'video' ? item.liveDateTime.toUTC().setLocale('fr') : item.liveDateTime && (item.type == 'live' || item.type == 'prelive') ? item.liveDateTime.setLocale('fr') : "";

            const liveDateTimeEn = String(itemLiveTimeEN?.toLocaleString(DateTime.DATE_FULL));
            const liveDateTimeFr = String(itemLiveTimeFR?.toLocaleString(DateTime.DATE_FULL));
            
            return (
                <li key={`v-items-grp-${i}`} itemProp='itemListElement'>
                        <div className="latest-container__item">
                            <a href={item.url} className="latest-container__item__img" tabIndex={-1}>
                                <img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title} itemProp='image'/>
                                {item.videoDuration && <div className="latest-container__item__img__date-container" aria-hidden="true">
                                    {item.videoDuration}
                                </div>}
                                {item.testStream === "true" && <div className='testFlag'>TEST</div>}
                            </a>
                            <div className="latest-container__item__info">
                                <a href={item.categoryURL} className="latest-container__item__info__category" itemProp='subjectOf' tabIndex={-1}>{item.category}</a>
                                <br/>
                                <div className="episode-date">
                                    {item.liveDateTime && lang == "en" && item.title != liveDateTimeEn && !item.title.includes(liveDateTimeEn) ? liveDateTimeEn : item.liveDateTime && lang == "fr" && !item.title.includes("1er") && item.title != liveDateTimeFr && !item.title.includes(liveDateTimeFr) ? liveDateTimeFr : ""}
                                </div>
                                <a href={item.url} className="latest-container__item__info__title" itemProp='description' aria-label={item.title ? item.title + " - " + item.category : ""}>
                                    {item.title && truncateWords(item.title, 88)}
                                    {item.title && item.title.length > 88 && (
                                        <span className="description__ellipsis">...</span>
                                    )}
                                </a>
                            </div>
                        </div>
                </li>
            )
        })
    }

    return (
        <VCarouselHolder>
            <div aria-labelledby="latest">
                <h2 id="latest" className="list-title" itemProp='name'>{itemList.title}</h2>
                <a className={`${itemList.paginated && itemList.seeallbtn ? "seeAllTopBtn" : "hidden"}`} href={itemList.seeAllUrl} aria-label={itemList.title ? getLabel("VIEW_ALL") + " " + itemList.title : ""}>
                    <GetLabel name="VIEW_ALL"/>
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="arrow-right" className="svg-inline--fa fa-arrow-right fa-w-14 " role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                        <path fill="currentColor" d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z"></path>
                    </svg>
                </a>
                <ul id="latest-container" className="latest-container" itemScope itemType='https://schema.org/ItemList'>
                    {renderItems()}
                </ul>
                {!itemList.paginated && itemList.seeallbtn && <div className="latest-container__btns-container">
                    <a href={itemList.seeAllUrl} className="seeAll-btn" aria-label={itemList.title ? getLabel("SEEALL") + " " + itemList.title : getLabel("SEEALL")}>
                        <GetLabel name="SEEALL"/>
                    </a></div>}
                {itemList.paginated && <div className={`${itemList.paginated && itemList.items.length > pageSize ? "latest-container__btns-container" : "hidden"}`}>
                    <button disabled={page === 0} onClick={showPrev} aria-controls="latest-container" aria-label={page !== 0 ? pageSize + " " + getLabel("PREVIOUSITEMS") : getLabel("NOPREVITEMS")}>
                        <svg aria-labelledby="svg-inline--fa-title-bVoow2Qlmudh" data-prefix="fas" data-icon="chevron-left"
                             className="svg-inline--fa fa-chevron-left fa-w-10 " role="img" xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 320 512" aria-hidden="true"><title id="svg-inline--fa-title-bVoow2Qlmudh">previous</title>
                            <path fill="currentColor"
                                  d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"></path>
                        </svg>
                        <GetLabel name="PREVIOUS"/>
                    </button>
                    <button disabled={isLastPage()} onClick={showNext} aria-controls="latest-container" aria-label={!isLastPage() ? pageSize + " " + getLabel("NEXTITEMS") : getLabel("NO_MORE_ITEMS")}>
                        <GetLabel name="NEXT"/>
                        <svg aria-labelledby="svg-inline--fa-title-SC4oNuPwHgp3" data-prefix="fas" data-icon="chevron-right"
                             className="svg-inline--fa fa-chevron-right fa-w-10 " role="img" xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 320 512" aria-hidden="true"><title id="svg-inline--fa-title-SC4oNuPwHgp3">next</title>
                            <path fill="currentColor"
                                  d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path>
                        </svg>
                    </button>
                    <p role="status" hidden>{!isLastPage() ? getLabel("MORE_ITEMS") : getLabel("NO_MORE_ITEMS")}</p>
                </div>}
            </div>
        </VCarouselHolder>
    );
};

export default VCarousel;